import { HeatContract, PutUtilityContract } from "@/api/facilities";
import RootModal from "@/components/RootModal/RootModal";
import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import {
  useGetFacilityProductMass,
  useGetHeatOptions,
  useInsertContract,
  useUpdateContract,
} from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { Checkbox, Col, DatePicker, Form, message, Row, Space, Spin } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import _ from "lodash";
import EstimationSwitch from "../EstimationSwitch";
import styles from "../Modal.module.scss";
import { constructUrn, extractUrn } from "./funcs";
import heatStyles from "./HeatContractModal.module.scss";
import HeatSource from "./HeatSource";

const { RangePicker } = DatePicker;

interface HeatContractForm {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  heatTypes: string[];
  estimation: boolean;
  sources: {
    urn: string;
    amount: number;
    unit: "mj" | "m3" | "kg";
  }[];
}

export interface HeatSourceLabels {
  category_label: string;
  source: string | undefined;
  source_label: string;
}

const SourcesOptions = ({
  mostUsedSources,
  otherSources,
  loading,
}: {
  mostUsedSources: HeatSourceLabels[];
  otherSources: HeatSourceLabels[];
  loading: boolean;
}) => {
  const translation = useTranslation();

  return (
    <>
      {loading && (
        <Space>
          <Spin size={"small"} />
          <span>{translation.facility.contract.heat.modal.step2LoadingText}</span>
        </Space>
      )}
      {!loading && (
        <Row className={heatStyles.sourceOptionsContainer}>
          <Col
            span={24}
            className={heatStyles.sourceOptionsMostUsedContainer}
          >
            <Row
              gutter={[10, 10]}
              className={heatStyles.sourceOptionsDetail}
            >
              <Col span={4}>
                <span className={heatStyles.sourceHeading}>MOST USED:</span>
              </Col>
              <Col span={20}>
                <Row gutter={[10, 10]}>
                  {mostUsedSources?.map((category) => (
                    <Col key={String(category.source)}>
                      <Checkbox value={category.source}>
                        <span className={heatStyles.sourceText}>{category?.source_label}</span>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row
              gutter={[10, 10]}
              className={heatStyles.sourceOptionsDetail}
            >
              <Col span={4}>
                <span className={heatStyles.sourceHeading}>OTHERS:</span>
              </Col>
              <Col span={20}>
                <Row gutter={[5, 10]}>
                  {otherSources?.map((category) => (
                    <Col key={String(category.source)}>
                      <Checkbox value={category.source}>
                        <span className={heatStyles.sourceText}>{category?.source_label}</span>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </>
  );
};

export default function HeatContractModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: HeatContract;
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const translation = useTranslation();
  const postContract = useInsertContract();
  const putContract = useUpdateContract();
  const [form] = useForm<HeatContractForm>();
  const analytics = useAnalytics({ enabled: !publishedId });
  const estimation = useWatch("estimation", form);
  const sources = useWatch("sources", form);

  const {
    data: optionsData,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useGetHeatOptions({ facilityId, enabled: open });

  const mostUsedSources: HeatSourceLabels[] = [
    { category_label: "NATURAL_GAS", source: "", source_label: "Natural gas" },
  ];

  const dateRange = useWatch("dateRange", form);
  const { data: productMass } = useGetFacilityProductMass({
    facilityId,
    startDate: dateRange ? dateRange[0] : minDate,
    endDate: dateRange ? dateRange[1] : maxDate,
  });

  const otherSources: HeatSourceLabels[] = _.chain(optionsData)
    // Filter out natural gas
    .filter(function (option) {
      return (
        !mostUsedSources.find(function (source) {
          return source.category_label === option.category_label;
        }) && option.source_label !== null
      );
    })

    // Extract source and source_label
    .map(function (option) {
      return {
        category_label: option.category_label,
        source: extractUrn(option.id).source,
        source_label: option.source_label,
      };
    })

    // Remove duplicates
    .uniqBy("source_label")
    // Sort by source_label, keep use average at first
    .sortBy(function (option) {
      return option.source_label === "Use average" ? "" : option.source_label;
    })
    .value();

  const allSources: HeatSourceLabels[] = [...mostUsedSources, ...otherSources];

  const handleError = (error: AxiosError<{ message: string }>) => {
    if (error?.response?.data?.message === "HEAT_ADDRESS_ERROR") {
      message.error(translation.facility.contract.heat.modal.geographyError);
    } else {
      message.error(translation.errors.general);
    }
  };
  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const contractToPutPost: PutUtilityContract = {
          facility_id: facilityId,
          end_date: values.dateRange[1].endOf("month").format("YYYY-MM-DD"),
          start_date: values.dateRange[0].startOf("month").format("YYYY-MM-01"),
          type: "heat",
          contract_type: values.estimation ? "estimate" : "actual",
          sources: values.sources.map((source: any) => ({
            urn: constructUrn({
              category: source.category,
              source: source.source,
              scope: source.scope,
              method: source.method,
              type: source.type,
            }),
            amount: source.amount,
            capital_owned: false,
            unit: source.unit,
            percentage: values.estimation ? 1 : source.percentage / 100,
          })),
        };

        if (contract) {
          putContract
            .mutateAsync({
              contractId: contract.id,
              contract: { id: contract.id, ...contractToPutPost },
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Updated");
                message.success("Heat contract updated.");
                closeModal();
              }
            })
            .catch((error) => handleError(error));
        } else {
          postContract
            .mutateAsync({
              contract: contractToPutPost,
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Added");
                message.success("Heat contract added.");
                closeModal();
              }
            })
            .catch((error) => handleError(error));
        }
      })
      .catch(() => {
        message.error(translation.facility.contract.heat.modal.validationError);
      });
  };

  const getSourcesFromContract = () => {
    return (
      contract?.sources.map((source) => {
        const extractedParts = extractUrn(source.urn);
        return {
          ...source,
          percentage: source.percentage * 100,
          category: extractedParts.category,
          source: extractedParts.source,
          method: extractedParts.method || null,
          type: extractedParts.type || null,
          scope: extractedParts.scope,
        };
      }) || []
    );
  };

  const getHeatTypesFromContract = () => {
    return contract?.sources.map((source) => extractUrn(source.urn).source).reverse() || [];
  };

  const getDateRangeFromContract = () => {
    return contract ? [dayjs(contract?.start_date), dayjs(contract?.end_date)] : [minDate, maxDate];
  };

  const getEstimationFromContract = () => {
    return contract ? contract.contract_type === "estimate" : false;
  };

  const handleHeatTypeChange = () => {
    const currentSources = form.getFieldValue("sources");
    const currentHeatTypes = form.getFieldValue("heatTypes");

    // check which heat types are not in current sources
    const sourcesToAdd = currentHeatTypes?.filter(
      (item: string) => !currentSources?.find((source: any) => source.source === item)
    );

    // check which sources are missing from heat types
    const sourcesToRemove = currentSources?.filter(
      (source: any) => !currentHeatTypes?.includes(source.source)
    );

    // remove deselected sources
    sourcesToRemove?.forEach((item: any) => {
      form.setFieldValue(
        "sources",
        currentSources?.filter((source: any) => source.source !== item.source)
      );
    });

    // append selected sources
    sourcesToAdd?.forEach((item: string) => {
      const sourceInOptions = allSources.find((source) => source.source === item);
      form.setFieldsValue({
        sources: [
          ...currentSources,
          {
            category: sourceInOptions?.category_label,
            source: sourceInOptions?.source,
            unit: "mj",
            amount: null,
            percentage: 100,
            urn: "",
            method: null,
            type: null,
            scope: "", // average pre-selected,
          },
        ],
      });
    });
  };

  return (
    <RootModal
      className={[styles.ContractModal, styles.heat].join(" ")}
      open={open}
      width={1000}
      onCancel={() => closeModal()}
      okText={"Save"}
      keyboard={false}
      title={translation.facility.contract.heat.modal.title}
      okButtonProps={{
        loading: postContract.isLoading || putContract.isLoading,
      }}
      onOk={onSubmit}
    >
      <Form
        form={form}
        onKeyUp={(e) => {
          if (e.code === "Enter") onSubmit();
        }}
        initialValues={{
          heatTypes: getHeatTypesFromContract(),
          sources: getSourcesFromContract(),
          dateRange: getDateRangeFromContract(),
          estimation: getEstimationFromContract(),
        }}
        onFinish={onSubmit}
      >
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles["step-heading"]}>
              {translation.facility.contract.heat.modal.step1}
            </div>
            <div className={styles["step-content"]}>
              <div>
                <Form.Item
                  name="dateRange"
                  rules={[
                    {
                      required: true,
                      message: "Date range is required.",
                    },
                  ]}
                >
                  <RangePicker picker="month" />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles["step-heading"]}>
              {translation.facility.contract.heat.modal.step2}
            </div>
            <div className={styles["step-subtext"]}>
              {translation.facility.contract.heat.modal.step2Description}
            </div>
            <div className={styles["step-content"]}>
              <div style={{ width: "100%" }}>
                <div className={styles["bold-radio"]}>
                  {!optionsError ? (
                    <Form.Item
                      name="heatTypes"
                      rules={[
                        {
                          required: true,
                          message: "Heat source is required.",
                        },
                      ]}
                    >
                      <Checkbox.Group onChange={handleHeatTypeChange}>
                        <SourcesOptions
                          otherSources={otherSources}
                          mostUsedSources={mostUsedSources}
                          loading={optionsLoading}
                        />
                      </Checkbox.Group>
                    </Form.Item>
                  ) : (
                    <div>{translation.facility.contract.heat.modal.optionsError}</div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: sources?.length > 0 ? "block" : "none" }}>
              <Row gutter={[20, 20]}>
                <Col span={24}>
                  <div className={styles["step-heading"]}>
                    {translation.facility.contract.heat.modal.step3}
                  </div>
                </Col>
              </Row>
              <div className={styles.sources}>
                <div className={styles.sourcesWrapper}>
                  <Form.List name="sources">
                    {(fields, { add, remove }, { errors }) => (
                      <>
                        {fields.map((field) => (
                          <Col
                            span={24}
                            key={field.key}
                          >
                            {optionsLoading ? (
                              <Skeleton style={{ width: "100%", height: "200px" }} />
                            ) : (
                              <HeatSource
                                allOptionsData={optionsData}
                                allSources={allSources}
                                field={field}
                                form={form}
                                facilityId={facilityId}
                                estimation={estimation}
                                productMass={productMass?.product_mass}
                              />
                            )}
                          </Col>
                        ))}
                      </>
                    )}
                  </Form.List>
                </div>
                <EstimationSwitch />
              </div>
            </div>
          </div>
        </div>
      </Form>
    </RootModal>
  );
}
